/**
 * **舞台の背景の器**（Issue #565）。`canvas/layers.js` の `background` 層（一番奥）に描く。
 *
 * - 持つ（器）: 舞台いっぱいに `cover` で敷く・トランスフォーム（部品 `background`）を当てる
 * - 持たない: **何を敷くか**（テーマの `background(style)`。バリエーションの選択も `style` の中）
 *
 * 絵は動かないので DOM の `<img>` で十分（読み終わりを待って描き直す必要も無い）。**時計を持たない**。
 */
import { resolveBackground } from '../core/theme';
import { placeBox, transformOf } from '../core/transform';

/**
 * そのテーマ・見た目の値での背景（`resolveBackground` を通したもの）。持たなければ null
 * @param theme `toTheme()` の結果
 * @param style `resolveStyle` を通した見た目の値
 */
export const backgroundOf = (theme, style) => resolveBackground(theme?.background?.(style ?? theme?.style ?? {}));

/**
 * @param bg `backgroundOf()` の結果
 * @param canvas 舞台の大きさ
 * @param style 見た目の値（部品 `background` のトランスフォームを読む）
 */
export default function BackgroundLayer({ bg, canvas, style = null }) {
  if (!bg) return null;
  const box = { x: 0, y: 0, w: canvas.w, h: canvas.h };
  const t = placeBox(box, transformOf(style, 'background'), 'screen');
  const dx = t.center[0] - canvas.w / 2;
  const dy = t.center[1] - canvas.h / 2;
  const transform = `translate(${dx}px, ${dy}px) rotate(${t.rot}rad) scale(${t.scale})`;
  return (
    <div className="de-layer" data-layer="background" style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: bg.color ?? undefined }}>
      {bg.image ? (
        <img
          alt=""
          data-testid="de-background"
          src={bg.image}
          draggable={false}
          style={{
            position: 'absolute', left: 0, top: 0, width: canvas.w, height: canvas.h,
            objectFit: 'cover',
            // 絵の focus の点を舞台の中心に（中央以外は近似。object-position は「絵の p% を箱の p% に」合わせる）
            objectPosition: `${bg.focus.x * 100}% ${bg.focus.y * 100}%`,
            transform, transformOrigin: 'center', opacity: t.opacity,
            // 明るさ・彩度（テーマの background が決める。1 ならフィルタを掛けない）
            filter: (bg.brightness ?? 1) !== 1 || (bg.saturate ?? 1) !== 1 ? `brightness(${bg.brightness ?? 1}) saturate(${bg.saturate ?? 1})` : undefined,
          }}
        />
      ) : null}
    </div>
  );
}
